import React, { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Bot, Settings } from "lucide-react";
import Sidebar from "@/layout/Sidebar";
import TopNavbar from "@/layout/TopNavbar";
import Analytics from "./Analytics";
import AEQueries from "./AEQueries";
import ChatSessions from "./ChatSessions";
import Sandbox from "./Sandbox";
import KnowledgeBase from "./KnowledgeBase";
import { getLocalStorageWithExpiry } from "@/utils";
import { VALID_ANSWER_ENGINE_TABS } from "@/const";
import "@/pages/page.css";

export default function AnswerEngine() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const isLegacySettings = requestedTab === "settings";
  const activeTab = isLegacySettings
    ? "sandbox"
    : VALID_ANSWER_ENGINE_TABS.has(requestedTab)
      ? requestedTab
      : "sandbox";

  const requestedView = searchParams.get("view");
  const sandboxView = isLegacySettings
    ? "settings"
    : requestedView === "settings"
      ? "settings"
      : "sandbox";

  const [isPermanentlyExpanded, setIsPermanentlyExpanded] = useState(true);
  const answerEngineEnabled = getLocalStorageWithExpiry(
    "answer_engine_enabled",
  );

  const [isSettingsDirty, setIsSettingsDirty] = useState(false);
  const isSettingsDirtyRef = useRef(false);

  // Canonicalize legacy /answer-engine?tab=settings to /answer-engine?tab=sandbox&view=settings
  useEffect(() => {
    if (requestedTab === "settings") {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", "sandbox");
          next.set("view", "settings");
          return next;
        },
        { replace: true },
      );
    }
  }, [requestedTab, setSearchParams]);

  const handleSettingsDirtyChange = useCallback((dirty) => {
    setIsSettingsDirty(dirty);
    isSettingsDirtyRef.current = dirty;
  }, []);

  const confirmDiscardSettings = useCallback(() => {
    if (!isSettingsDirtyRef.current || sandboxView !== "settings") {
      return true;
    }

    const confirmed = window.confirm(
      t(
        "answerEngine.confirmDiscardSettings",
        "You have unsaved changes in Settings. Are you sure you want to discard them?",
      ),
    );

    if (confirmed) {
      isSettingsDirtyRef.current = false;
      setIsSettingsDirty(false);
      return true;
    }
    return false;
  }, [sandboxView, t]);

  const handleBeforeNavbarAction = useCallback(() => {
    return confirmDiscardSettings();
  }, [confirmDiscardSettings]);

  // Intercept client-side link navigation (Sidebar, TopNavbar) when Settings has unsaved edits
  useEffect(() => {
    if (!isSettingsDirty || sandboxView !== "settings") return;

    const handleClickCapture = (e) => {
      // Ignore clicks with modifier keys (new tab, etc.)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
        return;
      }

      const anchor = e.target.closest("a[href]");
      if (!anchor) return;

      const href = anchor.getAttribute("href") || "";
      // Ignore in-page anchors, javascript triggers, downloads, or external targets
      if (
        !href ||
        href.startsWith("#") ||
        /^javascript:/i.test(href) ||
        anchor.target === "_blank" ||
        anchor.hasAttribute("download")
      ) {
        return;
      }

      // Check if the link target stays on the current settings view
      if (
        href === window.location.pathname + window.location.search ||
        href.includes("tab=sandbox&view=settings") ||
        href.includes("tab=settings")
      ) {
        return;
      }

      if (!confirmDiscardSettings()) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("click", handleClickCapture, { capture: true });

    return () => {
      document.removeEventListener("click", handleClickCapture, { capture: true });
    };
  }, [isSettingsDirty, sandboxView, confirmDiscardSettings]);

  const toggleSidebar = () => {
    setIsPermanentlyExpanded(!isPermanentlyExpanded);
  };

  const handleSandboxViewChange = (newView) => {
    if (newView === sandboxView) return;

    if (sandboxView === "settings" && isSettingsDirtyRef.current) {
      if (!confirmDiscardSettings()) {
        return;
      }
    }

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", "sandbox");
      if (newView === "settings") {
        next.set("view", "settings");
      } else {
        next.delete("view");
      }
      return next;
    });
  };

  return (
    <div className="app-container">
      <Sidebar
        currentPage="answer-engine"
        isPermanentlyExpanded={isPermanentlyExpanded}
      />
      <main className="content bg-page">
        <div className="container-fluid p-4">
          <TopNavbar
            isPermanentlyExpanded={isPermanentlyExpanded}
            toggleSidebar={toggleSidebar}
            onBeforeAction={handleBeforeNavbarAction}
          />

          {!answerEngineEnabled ? (
            <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 mt-4">
              <i
                className="bi bi-lock text-muted mb-3"
                style={{ fontSize: "48px" }}
              ></i>
              <h5 className="fw-semibold text-dark">
                {t(
                  "answerEngine.notEnabledTitle",
                  "Answer Engine Not Enabled",
                )}
              </h5>
              <p className="text-muted mt-2" style={{ maxWidth: "420px" }}>
                {t(
                  "answerEngine.notEnabledMessage",
                  "This feature is not enabled for your current project. Please contact support to enable Answer Engine Analytics for this project.",
                )}
              </p>
            </div>
          ) : (
            <>
              <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
                <div>
                  <h3 className="h4 fw-bold text-dark mb-0">
                    {t("answerEngine.title", "Answer Engine")}
                  </h3>
                  <p className="mt-1 fs-6 text-secondary">
                    {activeTab === "analytics"
                      ? t(
                          "answerEngine.analyticsSubtitle",
                          "Monitor performance and user engagement metrics",
                        )
                      : activeTab === "ae-queries"
                        ? t(
                            "answerEngine.aeQueriesSubtitle",
                            "Browse and inspect queries processed by the Answer Engine",
                          )
                        : activeTab === "chat-sessions"
                          ? t(
                              "answerEngine.chatSessionsSubtitle",
                              "Browse individual chat sessions and their full conversations",
                            )
                          : activeTab === "sandbox"
                            ? sandboxView === "settings"
                              ? t(
                                  "answerEngine.settingsSubtitle",
                                  "Configure your Answer Engine settings",
                                )
                              : t(
                                  "answerEngine.sandboxSubtitle",
                                  "Live preview with feedback & training tools",
                                )
                            : t(
                                "answerEngine.knowledgeBaseSubtitle",
                                "Manage your knowledge base URLs and pending ingestion requests",
                              )}
                  </p>
                </div>

                {activeTab === "sandbox" && (
                  <div
                    role="tablist"
                    aria-label={t("answerEngine.sandboxTabs", "Sandbox view modes")}
                    className="btn-group rounded-pill p-1 border shadow-sm ms-auto"
                    style={{ height: "40px", background: "#f3f4f6" }}
                    data-testid="sandbox-subtabs"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={sandboxView === "sandbox"}
                      onClick={() => handleSandboxViewChange("sandbox")}
                      className={`btn px-3 d-flex align-items-center gap-2 fw-semibold border-0 btn-sm ${
                        sandboxView === "sandbox" ? "active" : "text-secondary"
                      }`}
                      style={
                        sandboxView === "sandbox"
                          ? {
                              background: "#ffffff",
                              color: "var(--brand-navy)",
                              borderRadius: "9999px",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                            }
                          : { color: "#6b7280" }
                      }
                      data-testid="sandbox-tab-btn"
                    >
                      <Bot size={15} />
                      <span>{t("answerEngine.tabs.sandbox", "Sandbox")}</span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={sandboxView === "settings"}
                      onClick={() => handleSandboxViewChange("settings")}
                      className={`btn px-3 d-flex align-items-center gap-2 fw-semibold border-0 btn-sm ${
                        sandboxView === "settings" ? "active" : "text-secondary"
                      }`}
                      style={
                        sandboxView === "settings"
                          ? {
                              background: "#ffffff",
                              color: "var(--brand-navy)",
                              borderRadius: "9999px",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                            }
                          : { color: "#6b7280" }
                      }
                      data-testid="settings-tab-btn"
                    >
                      <Settings size={15} />
                      <span>{t("answerEngine.tabs.settings", "Settings")}</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="answer-engine-content">
                {activeTab === "analytics" ? (
                  <Analytics />
                ) : activeTab === "ae-queries" ? (
                  <AEQueries />
                ) : activeTab === "chat-sessions" ? (
                  <ChatSessions />
                ) : activeTab === "sandbox" ? (
                  <Sandbox
                    view={sandboxView}
                    onSettingsDirtyChange={handleSettingsDirtyChange}
                  />
                ) : (
                  <KnowledgeBase />
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
